import React from 'react';
import { ClipboardCheck, Utensils, Dumbbell, Brain } from 'lucide-react';
import { mockData } from '../mock';
import { Button } from './ui/button';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const iconMap = {
  'clipboard-check': ClipboardCheck,
  'utensils': Utensils,
  'dumbbell': Dumbbell,
  'brain': Brain
};

const VitalMethodSection = ({ onApplyClick }) => {
  const { vitalMethod } = mockData;
  const [headerRef, headerVisible] = useScrollAnimation();
  const [card1Ref, card1Visible] = useScrollAnimation();
  const [card2Ref, card2Visible] = useScrollAnimation();
  const [card3Ref, card3Visible] = useScrollAnimation();
  const [card4Ref, card4Visible] = useScrollAnimation();
  const [ctaRef, ctaVisible] = useScrollAnimation();

  return (
    <section id="method" className="relative py-12 md:py-24 bg-gradient-to-b from-gray-900 via-black to-gray-900">
      {/* Background Grid */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(95,155,199,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(95,155,199,0.02)_1px,transparent_1px)] bg-[size:64px_64px]"></div>
      
      {/* Glowing Orbs */}
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-[#5F9BC7]/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-[#FFE7AB]/10 rounded-full blur-3xl"></div>

      <div className="relative z-10 container mx-auto px-4">
        {/* Section Header */}
        <div 
          ref={headerRef}
          className={`scroll-animate fade-in-bottom ${headerVisible ? 'visible' : ''} text-center mb-20`}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            The Vital Method
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            {vitalMethod.subtitle}
          </p>
        </div>

        {/* Pillars Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto mb-16">
          {vitalMethod.pillars.map((pillar, index) => {
            const Icon = iconMap[pillar.icon];
            const animRefs = [card1Ref, card2Ref, card3Ref, card4Ref];
            const animVisible = [card1Visible, card2Visible, card3Visible, card4Visible];
            
            return (
              <div 
                key={pillar.id}
                ref={animRefs[index]}
                className={`scroll-animate pop-up stagger-${index + 1} ${animVisible[index] ? 'visible' : ''} group relative bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:border-[#5F9BC7]/50 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-[#5F9BC7]/20`}
              >
                {/* Corner Accent */}
                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-[#5F9BC7]/20 to-transparent rounded-bl-full blur-xl group-hover:from-[#5F9BC7]/30 transition-all duration-300"></div>
                
                {/* Icon */}
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#5F9BC7] to-[#FFE7AB] flex items-center justify-center shadow-lg shadow-[#5F9BC7]/30 group-hover:shadow-[#5F9BC7]/50 transition-all duration-300">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-[#5F9BC7] transition-colors duration-300">
                      {pillar.title}
                    </h3>
                    <div className="h-1 w-16 bg-gradient-to-r from-[#5F9BC7] to-[#FFE7AB] rounded-full mb-3"></div>
                  </div>
                </div>

                {/* Description */}
                <p className="text-gray-400 leading-relaxed">
                  {pillar.description}
                </p>

                {/* Hover Gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#5F9BC7]/0 to-[#5F9BC7]/0 group-hover:from-[#5F9BC7]/5 group-hover:to-transparent rounded-2xl transition-all duration-300 pointer-events-none"></div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div 
          ref={ctaRef}
          className={`scroll-animate fade-in-bottom ${ctaVisible ? 'visible' : ''} text-center`}
        >
          <div className="inline-block bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8">
            <h3 className="text-2xl font-bold text-white mb-4">
              Ready to Start Your Transformation?
            </h3>
            <p className="text-gray-400 mb-6 max-w-2xl">
              Join clients who have achieved sustainable fat loss with the Vital Method
            </p>
            <Button
              onClick={onApplyClick}
              size="lg"
              className="bg-gradient-to-r from-[#5F9BC7] to-[#4A8AB0] hover:from-[#4A8AB0] hover:to-[#5F9BC7] text-white px-8 py-6 text-lg rounded-xl font-semibold shadow-2xl shadow-[#5F9BC7]/40 transition-all duration-300 hover:shadow-[#5F9BC7]/60 hover:scale-105"
            >
              Apply for Coaching
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VitalMethodSection;
